import { useStores } from '@littlebird/sdk';
import { useCallback, useEffect, useRef } from 'react';

import { ModalsManager } from '@/components/modals/modalsManager';
import { resolveNewChatProjectId } from '@/features/chat/draft-ids';
import { CreateProjectModal } from '@/features/projects/create-project-modal';
import { useProjectActions } from '@/features/projects/project-actions';
import {
  type ProjectMenuOption,
  toProjectMenuOption,
} from '@/features/projects/project-menu-option';
import { NEW_CHAT_ID } from '@/utils/constants';

/**
 * Read the owning draft or thread; only synced-thread changes call the server.
 *
 * Taking two ids looks redundant, because for an existing chat they are the same
 * string. They only differ for a New Chat, where `threadId` is the bare sentinel
 * and `draftId` names the composer it came from - `tab:<id>` or `project:<id>`.
 *
 * A project page's New Chat needs no separate `projectId`: its draft id already
 * carries the project, and `resolveNewChatProjectId` reads it back out.
 */
export const useComposerProject = ({
  draftId,
  threadId,
}: {
  draftId: string;
  threadId: string;
}) => {
  const { drafts, projects, threads } = useStores();
  const { setProjectAssignment } = useProjectActions();
  // Tracks the current create-project dialog. `waitForServer` below means it
  // returns a round trip later, and the user can switch drafts in the meantime,
  // so this ref, the cleanup effect keyed on `draftId`, and the identity check
  // in `onCreate` together keep a dialog from assigning to a draft the user has
  // left.
  const createProjectModalRef = useRef<{ hide: () => void } | undefined>(
    undefined
  );

  const draft = drafts.getDraft(draftId);
  const thread = threads.getById(threadId);

  // Not `isNewChatDraftId`, which is false for this bare sentinel
  const hasNoThreadYet = threadId === NEW_CHAT_ID;
  const selectedProjectId = hasNoThreadYet
    ? resolveNewChatProjectId(draftId, draft?.projectId)
    : (thread?.projectId ?? null);
  // A project missing from the store is not evidence it was deleted: the list
  // loads lazily and holds one page. Deletion clears the id at its source
  const selectedProject = selectedProjectId
    ? (projects.getById(selectedProjectId) ?? null)
    : null;
  const projectOptions: ProjectMenuOption[] = projects.orderedData.map(
    (project) => toProjectMenuOption(project, selectedProjectId === project.id)
  );

  // Where a selection is written depends on what owns it:
  // - No thread yet: the draft holds it until the chat is created.
  // - Synced thread: the server owns it, so persist through the API.
  // - Unsynced thread: it exists only here, so write it locally.
  const handleSelectProject = useCallback(
    async (nextProjectId: string | null) => {
      if (hasNoThreadYet) {
        await drafts.updateDraft({ draftId, projectId: nextProjectId });
        return;
      }
      // Decide from the live thread, not this render's snapshot: the create
      // dialog can invoke this after the thread synced while it was open
      if (threads.getById(threadId)?.synced === true) {
        await setProjectAssignment(
          { kind: 'thread', id: threadId },
          nextProjectId
        );
        return;
      }
      threads.setProjectIdLocal(threadId, nextProjectId);
    },
    [hasNoThreadYet, draftId, drafts, setProjectAssignment, threadId, threads]
  );

  // Forget the dialog before hiding it, so a result arriving mid-close is
  // already disowned. Composer closes it directly too, which is why this is
  // returned rather than kept private.
  const closeCreateProjectModal = useCallback(() => {
    const modal = createProjectModalRef.current;
    createProjectModalRef.current = undefined;
    modal?.hide();
  }, []);

  useEffect(() => {
    // Keyed on `draftId`: switching drafts closes and disowns the dialog opened
    // for the draft being left, so it cannot modify the one arrived at
    return closeCreateProjectModal;
  }, [closeCreateProjectModal, draftId]);

  const handleCreateProject = useCallback(() => {
    closeCreateProjectModal();
    const controls = ModalsManager.show(CreateProjectModal, {
      navigateOnCreate: false,
      waitForServer: true,
      onCreate: (project) => {
        // Ignore callbacks from a dialog closed or replaced for another draft
        if (createProjectModalRef.current !== controls) return;
        return handleSelectProject(project.id);
      },
    });
    createProjectModalRef.current = controls;
  }, [closeCreateProjectModal, handleSelectProject]);

  return {
    selectedProject,
    selectedProjectId,
    projectOptions,
    handleSelectProject,
    handleCreateProject,
    closeCreateProjectModal,
  };
};
